import { anyLeague } from "../../classificators/league/index.js";
import { BaseballResultsProcessor, RankingStat } from "../../entities/index.js";
import { BattingRankingStats, PitchData, PitchingRankingStats, RankingScopeContext, ResultData } from "../../enums/index.js";
import EnumValueProvider from "../../providers/EnumValueProvider.js";
import MultiRankingStatCalculator from "./MultiRankingStatCalculator.js";
import NumberFormatter from "../../formatters/NumberFormatter.js";
import ResultsSlice from "../../valueObjects/ResultsSlice.js";
import resultsSliceForPitchType from "../../classificators/resultsSlice/resultsSliceForPitchType.js";
import ContextualFilterBase from "../../filters/contextual/ContextualFilterBase.js";
import ClassifiedPitchesFilter from "../../filters/contextual/ClassifiedPitchesFilter.js";
import satisfiesAll from "../../classificators/satisfiesAll.js";
import pitchingScopeContext from "../../classificators/resultsSlice/pitchingScopeContext.js";
import sessionWithOldTaggingData from "../../classificators/session/sessionWithOldTaggingData.js";
import PlateDisciplineStatsCalculator from "../PlateDisciplineStatsCalculator.js";
import battingScopeContext from "../../classificators/resultsSlice/battingScopeContext.js";
import RankingStatCaseVerifier from "../../validators/RankingStatCaseVerifier.js";
import ResultsFormatterBase from "../../formatters/ResultsFormatterBase.js";
import BatchResultsFormatter from "../../formatters/resultsFormatters/BatchResultsFormatter.js";
import DefaultValuesFormatter from "../../formatters/resultFormatters/DefaultValuesFormatter.js";
import StrikeZoneFrontFormatter from "../../formatters/resultFormatters/StrikeZoneFrontFormatter.js";
import PropertyAssuranceFormatter from "../../formatters/resultFormatters/PropertyAssuranceFormatter.js";
import GenericResultFormatter from "../../formatters/resultsFormatters/GenericResultFormatter.js";
import StrikeZoneSectorFormatter from "../../formatters/resultsFormatters/StrikeZoneSectorFormatter.js";
import CompositeResultsFormatter from "../../formatters/resultsFormatters/CompositeResultsFormatter.js";
import SessionResultsStructureFormatter from "../../formatters/resultsFormatters/SessionResultsStructureFormatter.js";
import StrikeZoneModelBase from "../../classificators/strikeZoneModel/StrikeZoneModelBase.js";

class PlateDisciplineCalculator extends MultiRankingStatCalculator{
    constructor(context, strikeZoneModel, sessionResultsFormatter, customResultsProcessor, plateDisciplineStatsCalculator, rankingStatCaseVerifier, numberFormatter, skipCache, contextualResultFilter) {
        if (typeof(customResultsProcessor) !== 'undefined' && !(customResultsProcessor instanceof BaseballResultsProcessor)) {
            throw new TypeError('customResultsProcessor must be an instance inheriting from BaseballResultsProcessor class');
        }
        if (typeof(numberFormatter) !== 'undefined' && !(numberFormatter instanceof NumberFormatter)) {
            throw new TypeError('numberFormatter must be an instance inheriting from NumberFormatter class');
        }
        if (typeof(contextualResultFilter) !== 'undefined' && !(contextualResultFilter instanceof ContextualFilterBase)) {
            throw new TypeError('contextualResultFilter must be an instance inheriting from ContextualFilterBase class');
        }
        if (typeof(plateDisciplineStatsCalculator) !== 'undefined' && !(plateDisciplineStatsCalculator instanceof PlateDisciplineStatsCalculator)) {
            throw new TypeError('plateDisciplineStatsCalculator must be an instance inheriting from PlateDisciplineStatsCalculator class');
        }
        if (typeof(sessionResultsFormatter) !== 'undefined' && !(sessionResultsFormatter instanceof ResultsFormatterBase)) {
            throw new TypeError('sessionResultsFormatter must be an instance inheriting from ResultsFormatterBase class');
        }
        if (!(strikeZoneModel instanceof StrikeZoneModelBase)) {
            throw new TypeError(`strikeZoneModel must inherit from StrikeZoneModelBase`);
        }
        if (!context || typeof(context) !== 'string') {
            throw new TypeError('context must be an non empty string');
        }
        if (![RankingScopeContext.Pitching.key, RankingScopeContext.Batting.key].find(s => s === context)) {
            throw new Error(`type of context ${context} in not supported, only ${RankingScopeContext.Pitching.key} and ${RankingScopeContext.Batting.key} are supported`);
        }

        super(
            context === RankingScopeContext.Pitching.key 
                ? [
                    PitchingRankingStats.Swings, PitchingRankingStats.Whiffs, PitchingRankingStats.Contacts,
                    PitchingRankingStats.KZonePitches, PitchingRankingStats.OZonePitches,
                    PitchingRankingStats.ZContacts, PitchingRankingStats.ZSwings, PitchingRankingStats.ZWhiffs,
                    PitchingRankingStats.OContacts, PitchingRankingStats.OSwings, PitchingRankingStats.OWhiffs,
                    // rates, optional...
                    PitchingRankingStats.SwingRate, PitchingRankingStats.WhiffRate, PitchingRankingStats.ContactRate,
                    PitchingRankingStats.SwingingStrikeRate, PitchingRankingStats.KZoneRate,
                    PitchingRankingStats.ZContactRate, PitchingRankingStats.ZSwingRate, PitchingRankingStats.ZWhiffRate,
                    PitchingRankingStats.OContactRate, PitchingRankingStats.OSwingRate, PitchingRankingStats.OWhiffRate,
                ]
                : [
                    BattingRankingStats.Swings, BattingRankingStats.Whiffs, BattingRankingStats.Contacts,
                    BattingRankingStats.KZonePitches, PitchingRankingStats.OZonePitches,
                    BattingRankingStats.ZContacts, BattingRankingStats.ZSwings, BattingRankingStats.ZWhiffs,
                    BattingRankingStats.OContacts, BattingRankingStats.OSwings, BattingRankingStats.OWhiffs,
                    // rates, optional...
                    BattingRankingStats.SwingRate, BattingRankingStats.WhiffRate, BattingRankingStats.ContactRate,
                    BattingRankingStats.SwingingStrikeRate, BattingRankingStats.KZoneRate,
                    BattingRankingStats.ZContactRate, BattingRankingStats.ZSwingRate, BattingRankingStats.ZWhiffRate,
                    BattingRankingStats.OContactRate, BattingRankingStats.OSwingRate, BattingRankingStats.OWhiffRate,
                ],
            rankingStatCaseVerifier || new RankingStatCaseVerifier(
                anyLeague, 
                sessionWithOldTaggingData,
                satisfiesAll(
                    context === RankingScopeContext.Pitching.key ? pitchingScopeContext: battingScopeContext,
                    resultsSliceForPitchType
                )
            ),
            skipCache
        );
    
        this.numberFormatter = numberFormatter || new NumberFormatter(3);
        this.resultsProcessor = customResultsProcessor || new BaseballResultsProcessor(
            new EnumValueProvider()
        );
        this.contextualFilter = contextualResultFilter || new ClassifiedPitchesFilter(this.resultsProcessor);
        this.plateDisciplineStatsCalculator = plateDisciplineStatsCalculator || new PlateDisciplineStatsCalculator();
        this.context = context;
        this.strikeZoneModel = strikeZoneModel;
        this.sessionResultsFormatter = sessionResultsFormatter || this.buildDefaultFormatter();
    }

    buildDefaultFormatter() {
        const pitchFormatters = [
            new DefaultValuesFormatter(PitchData.StrikeZoneFront)
                .registerReplacement('0; 0; 0', undefined)
                .registerReplacement(';;', undefined),
            new StrikeZoneFrontFormatter(),
        ];
        const hitFormatters = [];

        const formatters = [
            new SessionResultsStructureFormatter(true),
            new BatchResultsFormatter(new PropertyAssuranceFormatter(
                [ResultData.Data.key], (value) => (typeof(value) === 'object' && value !== null), {}
            )),
            new GenericResultFormatter(pitchFormatters, hitFormatters),
            new StrikeZoneSectorFormatter(this.strikeZoneModel)
        ];

        return new CompositeResultsFormatter(...formatters);
    }

    /**
     * Calculates given statistical parameter when it is not avaialble in cache
     * @param {ResultsSlice} slice - result slice with scope
     * @param {object} session - session object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/session-v2.json
     * @param {object} league - league object, @see https://bitbucket.org/flightscope/dto-schema/src/master/baseball/entity/league.json
     * @returns {object} object with keys equal to ranking stat parameter name and RankingStat instance as value
     */
    forceCalculate(slice, session, league) {
        if (!(slice instanceof ResultsSlice)) {
            throw new TypeError('slice must be an instance inheriting from ResultsSlice class');
        }

        const filteredResults = this.filterResults(slice.getSmallestSlice(), session, league);
        const formattedResults = this.resultsProcessor.applyFormatting(filteredResults, this.sessionResultsFormatter);
        const plateDiscipline = this.plateDisciplineStatsCalculator.calculatePlateDiscipline(
            formattedResults, true, this.context === RankingScopeContext.Pitching.key
        );
        const output = {};

        this.includeSwings(output, plateDiscipline);
        this.includeWhiffs(output, plateDiscipline);
        this.includeContacts(output, plateDiscipline);
        this.includeSwingingStrikes(output, plateDiscipline);
        this.includeZoneStats(output, plateDiscipline);
        this.includeInZoneStats(output, plateDiscipline);
        this.includeOutZoneStats(output, plateDiscipline);

        return output;
    }

    includeSwings(output, plateDiscipline) {
        const swings = this.supportedParams[0];
        if (plateDiscipline.hasOwnProperty(swings.key) && plateDiscipline[swings.key]) {
            output[swings.key] = RankingStat.fromParameter(
                swings,
                this.numberFormatter.format(plateDiscipline[swings.key]),
                plateDiscipline[swings.key]
            );
        }

        const weight = plateDiscipline.PitchesWithStrikeZonePosition;
        const swingRate = this.supportedParams[11];
        if (!Number.isNaN(plateDiscipline[swingRate.key]) && weight) {
            output[swingRate.key] = RankingStat.fromParameter(
                swingRate,
                this.numberFormatter.format(plateDiscipline[swingRate.key]),
                weight
            );
        }
    }

    includeSwingingStrikes(output, plateDiscipline) {
        const swingingStrikeRate = this.supportedParams[14];
        const weight = this.context === RankingScopeContext.Pitching.key
            ? PitchingRankingStats.BattersFaced
            : BattingRankingStats.PlateAppearances
        ;

        if (!Number.isNaN(plateDiscipline[swingingStrikeRate.key]) && plateDiscipline[weight.key]) {
            output[swingingStrikeRate.key] = RankingStat.fromParameter(
                swingingStrikeRate,
                this.numberFormatter.format(plateDiscipline[swingingStrikeRate.key]),
                plateDiscipline[weight.key]
            );
        }
    }

    includeWhiffs(output, plateDiscipline) {
        const whiffs = this.supportedParams[1];
        const weight = (this.context === RankingScopeContext.Pitching.key ? PitchingRankingStats: BattingRankingStats).Swings;
        if (plateDiscipline.hasOwnProperty(whiffs.key) && plateDiscipline[whiffs.key]) {
            output[whiffs.key] = RankingStat.fromParameter(
                whiffs,
                this.numberFormatter.format(plateDiscipline[whiffs.key]),
                plateDiscipline[weight.key]
            );
        }

        const whiffRate = this.supportedParams[12];
        if (!Number.isNaN(plateDiscipline[whiffRate.key]) && plateDiscipline[weight.key]) {
            output[whiffRate.key] = RankingStat.fromParameter(
                whiffRate,
                this.numberFormatter.format(plateDiscipline[whiffRate.key]),
                plateDiscipline[weight.key]
            );
        }
    }

    includeContacts(output, plateDiscipline) {
        const contacts = this.supportedParams[2];
        const weight = (this.context === RankingScopeContext.Pitching.key ? PitchingRankingStats: BattingRankingStats).Swings;
        if (plateDiscipline.hasOwnProperty(contacts.key) && plateDiscipline[contacts.key]) {
            output[contacts.key] = RankingStat.fromParameter(
                contacts,
                this.numberFormatter.format(plateDiscipline[contacts.key]),
                plateDiscipline[weight.key]
            );
        }

        const contactRate = this.supportedParams[13];
        if (!Number.isNaN(plateDiscipline[contactRate.key]) && plateDiscipline[weight.key]) {
            output[contactRate.key] = RankingStat.fromParameter(
                contactRate,
                this.numberFormatter.format(plateDiscipline[contactRate.key]),
                plateDiscipline[weight.key]
            );
        }
    }

    includeZoneStats(output, plateDiscipline) {
        const kZonePitches = this.supportedParams[3];
        if (!Number.isNaN(plateDiscipline['KZone']) && plateDiscipline['KZone']) {
            output[kZonePitches.key] = RankingStat.fromParameter(
                kZonePitches,
                this.numberFormatter.format(plateDiscipline['KZone']),
                plateDiscipline['KZone']
            );
        }

        const oZonePitches = this.supportedParams[4];
        if (!Number.isNaN(plateDiscipline['OZone']) && plateDiscipline['OZone']) {
            output[oZonePitches.key] = RankingStat.fromParameter(
                oZonePitches,
                this.numberFormatter.format(plateDiscipline['OZone']),
                plateDiscipline['OZone']
            );
        }

        const kZoneRate = this.supportedParams[15];
        const weight = plateDiscipline.PitchesWithStrikeZonePosition;
        if (!Number.isNaN(plateDiscipline[kZoneRate.key]) && weight) {
            output[kZoneRate.key] = RankingStat.fromParameter(
                kZoneRate,
                this.numberFormatter.format(plateDiscipline[kZoneRate.key]),
                weight
            );
        }
    }

    includeInZoneStats(output, plateDiscipline) {
        const zContacts = this.supportedParams[5];
        let weight = 'ZSwings';
        if (!Number.isNaN(plateDiscipline[zContacts.key]) && plateDiscipline[zContacts.key]) {
            output[zContacts.key] = RankingStat.fromParameter(
                zContacts,
                this.numberFormatter.format(plateDiscipline[zContacts.key]),
                plateDiscipline[weight]
            );
        }

        const zContactRate = this.supportedParams[16];
        if (!Number.isNaN(plateDiscipline[zContactRate.key]) && plateDiscipline[weight]) {
            output[zContactRate.key] = RankingStat.fromParameter(
                zContactRate,
                this.numberFormatter.format(plateDiscipline[zContactRate.key]),
                plateDiscipline[weight]
            );
        }

        const zSwings = this.supportedParams[6];
        weight = 'KZone';
        if (!Number.isNaN(plateDiscipline[zSwings.key]) && plateDiscipline[zSwings.key]) {
            output[zSwings.key] = RankingStat.fromParameter(
                zSwings,
                this.numberFormatter.format(plateDiscipline[zSwings.key]),
                plateDiscipline[weight]
            );
        }

        const zSwingRate = this.supportedParams[17];
        if (!Number.isNaN(plateDiscipline[zSwingRate.key]) && plateDiscipline[weight]) {
            output[zSwingRate.key] = RankingStat.fromParameter(
                zSwingRate,
                this.numberFormatter.format(plateDiscipline[zSwingRate.key]),
                plateDiscipline[weight]
            );
        }

        const zWhiffs = this.supportedParams[7];
        weight = 'ZSwings';
        if (!Number.isNaN(plateDiscipline[zWhiffs.key]) && plateDiscipline[zWhiffs.key]) {
            output[zWhiffs.key] = RankingStat.fromParameter(
                zWhiffs,
                this.numberFormatter.format(plateDiscipline[zWhiffs.key]),
                plateDiscipline[weight]
            );
        }

        const zWhiffRate = this.supportedParams[18];
        if (!Number.isNaN(plateDiscipline[zWhiffRate.key]) && plateDiscipline[weight]) {
            output[zWhiffRate.key] = RankingStat.fromParameter(
                zWhiffRate,
                this.numberFormatter.format(plateDiscipline[zWhiffRate.key]),
                plateDiscipline[weight]
            );
        }
    }

    includeOutZoneStats(output, plateDiscipline) {
        const oContacts = this.supportedParams[8];
        let weight = 'OSwings';
        if (!Number.isNaN(plateDiscipline[oContacts.key]) && plateDiscipline[oContacts.key]) {
            output[oContacts.key] = RankingStat.fromParameter(
                oContacts,
                this.numberFormatter.format(plateDiscipline[oContacts.key]),
                plateDiscipline[weight]
            );
        }

        const oContactRate = this.supportedParams[19];
        if (!Number.isNaN(plateDiscipline[oContactRate.key]) && plateDiscipline[weight]) {
            output[oContactRate.key] = RankingStat.fromParameter(
                oContactRate,
                this.numberFormatter.format(plateDiscipline[oContactRate.key]),
                plateDiscipline[weight]
            );
        }

        const oSwings = this.supportedParams[9];
        weight = 'OZone';
        if (!Number.isNaN(plateDiscipline[oSwings.key]) && plateDiscipline[oSwings.key]) {
            output[oSwings.key] = RankingStat.fromParameter(
                oSwings,
                this.numberFormatter.format(plateDiscipline[oSwings.key]),
                plateDiscipline[weight]
            );
        }

        const oSwingRate = this.supportedParams[20];
        if (!Number.isNaN(plateDiscipline[oSwingRate.key]) && plateDiscipline[weight]) {
            output[oSwingRate.key] = RankingStat.fromParameter(
                oSwingRate,
                this.numberFormatter.format(plateDiscipline[oSwingRate.key]),
                plateDiscipline[weight]
            );
        }

        const oWhiffs = this.supportedParams[10];
        weight = 'OSwings';
        if (!Number.isNaN(plateDiscipline[oWhiffs.key]) && plateDiscipline[oWhiffs.key]) {
            output[oWhiffs.key] = RankingStat.fromParameter(
                oWhiffs,
                this.numberFormatter.format(plateDiscipline[oWhiffs.key]),
                plateDiscipline[weight]
            );
        }

        const oWhiffRate = this.supportedParams[21];
        if (!Number.isNaN(plateDiscipline[oWhiffRate.key]) && plateDiscipline[weight]) {
            output[oWhiffRate.key] = RankingStat.fromParameter(
                oWhiffRate,
                this.numberFormatter.format(plateDiscipline[oWhiffRate.key]),
                plateDiscipline[weight]
            );
        }
    }

    filterResults(results, session, league) {
        return this.contextualFilter.filter(results, session, league);
    }
}

export default PlateDisciplineCalculator;